import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import * as cognitoAuth from "services/cognitoAuth";
import { getTokenContext } from "utils/tokenUtils";
import {
  getUserProfile,
  getUser,
  getAirportById,
  getNonProfitById,
} from "layouts/dashboardLayout/pages/Users/services/graphqlService";

const AuthContext = createContext(null);

/** sessionStorage flag for read-only guest demo (no Cognito session). */
export const GUEST_DEMO_SESSION_KEY = "avipair_guest_demo";

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [associations, setAssociations] = useState([]);
  const [isGuest, setIsGuest] = useState(() => {
    try {
      return sessionStorage.getItem(GUEST_DEMO_SESSION_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [error, setError] = useState(null);

  // Real Cognito session always wins over a stale guest flag in sessionStorage.
  useEffect(() => {
    if (user?.id_token) {
      try {
        sessionStorage.removeItem(GUEST_DEMO_SESSION_KEY);
      } catch {
        /* ignore */
      }
      setIsGuest(false);
    }
  }, [user?.id_token]);

  // Check for existing session on mount
  useEffect(() => {
    checkAuth().catch(() => {
      // Silently handle "No user found" - this is expected when user is not logged in
      // Error is already handled in checkAuth function
    });
  }, []);

  // Fetch user profile when authenticated. Returns profile data (or null) so callers can check e.g. feature list.
  const fetchUserProfile = useCallback(async (idToken) => {
    if (!idToken) {
      setProfile(null);
      setAssociations([]);
      return null;
    }

    try {
      setIsLoadingProfile(true);
      const result = await getUserProfile(idToken);
      if (result.success && result.data) {
        const profileData = result.data;
        setProfile(profileData);

        // Fetch full user (AWSJSON) to get association list for profile selector
        if (profileData?.profileId) {
          try {
            const userResult = await getUser(profileData.profileId, idToken);
            const raw =
              userResult?.data?.association ?? userResult?.data?.associations;
            const list = Array.isArray(raw) ? raw : [];

            // Enrich associations with entity / organization names so ProfileSelector
            // can render "<Entity Name> · <Entity Type> · <Role>" on first paint.
            const enriched = await Promise.all(
              list.map(async (a) => {
                if (!a?.entityId || !a?.entityType) return a;
                const type = String(a.entityType).toLowerCase();
                try {
                  if (type === "nonprofit") {
                    const res = await getNonProfitById(a.entityId, idToken);
                    const np = res?.data || {};
                    return np.name ? { ...a, entityName: np.name } : a;
                  }
                  if (type === "airport") {
                    const res = await getAirportById(a.entityId, idToken);
                    const ap = res?.data || {};
                    return ap.name ? { ...a, entityName: ap.name } : a;
                  }
                } catch {
                  // Ignore individual failures; leave association unchanged.
                }
                return a;
              })
            );

            setAssociations(enriched);
          } catch {
            setAssociations([]);
          }
        } else {
          setAssociations([]);
        }
        return profileData;
      } else {
        setProfile(null);
        setAssociations([]);
        return null;
      }
    } catch (err) {
      console.error("Failed to fetch user profile:", err);
      setProfile(null);
      setAssociations([]);
      return null;
    } finally {
      setIsLoadingProfile(false);
    }
  }, []);

  // Fetch profile when user is authenticated
  useEffect(() => {
    if (user?.id_token) {
      fetchUserProfile(user.id_token);
    } else {
      setProfile(null);
      setAssociations([]);
    }
  }, [user?.id_token, fetchUserProfile]);

  // Current context from token (custom:role, custom:entityType, custom:entityId).
  // Menus and UI use this; re-evaluated whenever id_token changes (e.g. after role switch + refresh).
  const tokenContext = useMemo(
    () => getTokenContext(user?.id_token),
    [user?.id_token]
  );
  const tokenRole = tokenContext.role;
  const tokenEntityType = tokenContext.entityType;
  const tokenEntityId = tokenContext.entityId;
  const tokenProfileId = tokenContext.profileId;

  // Set up token refresh interval
  useEffect(() => {
    if (user && user.refresh_token) {
      const interval = setInterval(() => {
        refreshAuth();
      }, 55 * 60 * 1000); // Refresh every 55 minutes (tokens typically expire in 1 hour)

      return () => clearInterval(interval);
    }
  }, [user]);

  const checkAuth = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const currentUser = await cognitoAuth.getCurrentUser();
      setUser(currentUser);
      return currentUser;
    } catch (err) {
      setUser(null);
      // Don't set error for "no user found" - that's normal when user is not logged in
      if (
        err.message !== "No user found" &&
        err.message !== "Invalid session"
      ) {
        setError(err);
        // Only throw non-expected errors
        throw err;
      }
      // For expected errors (no user found), return null instead of throwing
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAuth = async () => {
    try {
      if (user) {
        try {
          const refreshedUser = await cognitoAuth.refreshToken();
          setUser(refreshedUser);
          return refreshedUser;
        } catch (sdkErr) {
          // SDK has no session (e.g. after SetNewPassword via fetch) - use refresh token directly
          if (
            (sdkErr?.message === "No user found" ||
              sdkErr?.message === "Invalid session") &&
            user?.refresh_token
          ) {
            const refreshedUser =
              await cognitoAuth.refreshTokenWithRefreshToken(
                user.refresh_token
              );
            setUser(refreshedUser);
            return refreshedUser;
          }
          throw sdkErr;
        }
      }
      const currentUser = await cognitoAuth.getCurrentUser();
      setUser(currentUser);
      return currentUser;
    } catch (err) {
      console.error("Token refresh failed:", err);
      try {
        const currentUser = await cognitoAuth.getCurrentUser();
        setUser(currentUser);
        return currentUser;
      } catch (e) {
        // Same fallback when getCurrentUser fails
        if (
          (e?.message === "No user found" ||
            e?.message === "Invalid session") &&
          user?.refresh_token
        ) {
          try {
            const refreshedUser =
              await cognitoAuth.refreshTokenWithRefreshToken(
                user.refresh_token
              );
            setUser(refreshedUser);
            return refreshedUser;
          } catch (refreshErr) {
            console.error("Refresh token fallback failed:", refreshErr);
          }
        } else if (e?.message !== "No user found") {
          console.error("Session check failed:", e);
        }
        signOut();
        return null;
      }
    }
  };

  const signInAsGuest = useCallback(() => {
    try {
      sessionStorage.setItem(GUEST_DEMO_SESSION_KEY, "1");
    } catch {
      // ignore quota / private mode
    }
    setIsGuest(true);
    setError(null);
  }, []);

  const signIn = async (email, password) => {
    try {
      setIsLoading(true);
      setError(null);
      try {
        sessionStorage.removeItem(GUEST_DEMO_SESSION_KEY);
      } catch {
        /* ignore */
      }
      setIsGuest(false);
      const userData = await cognitoAuth.signIn(email, password);
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = useCallback(() => {
    cognitoAuth.signOut();
    setUser(null);
    setProfile(null);
    setAssociations([]);
    setError(null);
    try {
      sessionStorage.removeItem(GUEST_DEMO_SESSION_KEY);
    } catch {
      /* ignore */
    }
    setIsGuest(false);
  }, []);

  // Wrapper so callers (e.g. SetNewPassword) can set user and clear any stale error
  const setUserAndClearError = useCallback((userData) => {
    setUser(userData);
    if (userData) {
      setError(null);
    }
  }, []);

  const value = {
    user,
    profile,
    associations,
    tokenRole,
    tokenEntityType,
    tokenEntityId,
    tokenProfileId,
    isLoading,
    isLoadingProfile,
    error,
    isGuest,
    isAuthenticated: !!user,
    signIn,
    signInAsGuest,
    signOut,
    refreshAuth,
    checkAuth,
    fetchUserProfile,
    setUser: setUserAndClearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
